import { lazy, Suspense } from 'react'
import { Routes, Route } from 'react-router'
import { ProtectedLayout } from '@/layouts/protected-layout'
import { PublicLayout } from '@/layouts/public-layout'
import { AccountStatusLayout } from '@/layouts/account-status-layout'
import { ScrollToTop } from '@/shared/components/scroll-to-top'
import { PageLoader } from '@/shared/components/page-loader'

const AuthPage = lazy(() => import('./pages/auth'))
const AccountReviewPage = lazy(() => import('./pages/account-review'))
const AccessDeniedPage = lazy(() => import('./pages/access-denied'))
const HomePage = lazy(() => import('./pages/home'))
const EnquiriesPage = lazy(() => import('./pages/enquiries'))
const EnquiryDetailPage = lazy(() => import('./pages/enquiry-detail'))
const NewEnquiryPage = lazy(() => import('./pages/new-enquiry'))
const CasesPage = lazy(() => import('./pages/cases'))
const CaseDetailPage = lazy(() => import('./pages/case-detail'))
const NewCasePage = lazy(() => import('./pages/new-case'))
const InvoicesPage = lazy(() => import('./pages/invoices'))
const InvoiceDetailPage = lazy(() => import('./pages/invoice-detail'))
const NewInvoicePage = lazy(() => import('./pages/new-invoice'))
const EsignCompletePage = lazy(() => import('./pages/esign-complete'))
const ProfilePage = lazy(() => import('./pages/profile'))
const DsaCodesPage = lazy(() => import('./pages/dsa-codes'))
const OnboardingPage = lazy(() => import('./pages/onboarding'))
const NotificationsPage = lazy(() => import('./pages/notifications'))
const NotFoundPage = lazy(() => import('./pages/not-found'))

export function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <ScrollToTop />
      <Routes>
        <Route element={<PublicLayout />}>
          {/* One unified entry. Both paths resolve to it so existing links, bookmarks
              and anything the mobile app points at keep working. */}
          <Route path="/login" element={<AuthPage />} />
          <Route path="/signup" element={<AuthPage />} />
        </Route>

        {/* These two explain an account state, so they need the session loaded and need to
            react when it changes. Neither happens outside a layout. */}
        <Route element={<AccountStatusLayout />}>
          <Route path="/account-review" element={<AccountReviewPage />} />
          <Route path="/access-denied" element={<AccessDeniedPage />} />
        </Route>

        <Route element={<ProtectedLayout />}>
          <Route path="/" element={<HomePage />} />

          {/* Detail views are drawers over their list, so they are nested children.
              `new` is declared before `:id` so it is not swallowed by the param. */}
          <Route path="/enquiries" element={<EnquiriesPage />}>
            <Route path="new" element={<NewEnquiryPage />} />
            <Route path=":slug" element={<EnquiryDetailPage />} />
          </Route>

          <Route path="/cases" element={<CasesPage />}>
            <Route path="new" element={<NewCasePage />} />
            <Route path=":slug" element={<CaseDetailPage />} />
          </Route>

          {/* Where Cashfree returns a signer. It is a full page rather than a drawer over the
              invoice list, because its only job is to confirm the signature and close its own
              tab. It is declared before the list so it is not treated as an invoice id. */}
          <Route path="/invoices/signed/:caseId" element={<EsignCompletePage />} />

          <Route path="/invoices" element={<InvoicesPage />}>
            <Route path="new/:caseId" element={<NewInvoicePage />} />
            <Route path=":id" element={<InvoiceDetailPage />} />
          </Route>

          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/dsa-codes" element={<DsaCodesPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  )
}
